import { Box, Link, TextField } from '@vapor/material'
import { FormItem } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { useValidateItalianFiscalCodeLazyQuery } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { PersonForm } from '../person/person.form.hook'
import { CompanyForm } from '../company/company.form.hook'
import useGenerateItalianFiscalCode from './generate-fiscal-code'

export default function FiscalCodeInput({
  value,
  onChange,
  errorMessage,
  disabled,
  customer,
}: {
  value: string
  onChange: (value: string) => void
  errorMessage?: string
  disabled?: boolean
  customer: PersonForm | CompanyForm
}) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const [checkFiscalCode] = useValidateItalianFiscalCodeLazyQuery()
  const generateFiscalCode = useGenerateItalianFiscalCode()

  return (
    <FormItem
      fullWidth
      fieldLabel={t('pms.common.customer.fiscal_code')}
      errorMessage={errorMessage}
      fieldEndIcon={
        <Box display={'flex'} alignItems={'center'} gap={1}>
          {!('companyName' in customer) && (
            <Link
              onClick={async () => {
                const result = await generateFiscalCode(customer)
                if (result.error) {
                  return showError(result.error)
                }

                if (result.fiscalCode) {
                  onChange(result.fiscalCode)
                  showSuccess(t('pms.common.customer.fiscal_code_generated'))
                }
              }}
            >
              {t('pms.common.customer.fiscal_code_generate')}
            </Link>
          )}
          <Link
            onClick={async () => {
              if (!value) {
                return showError(t('pms.common.customer.missing_fiscal_code'))
              }

              const { data, error } = await checkFiscalCode({ variables: { fiscalCode: value } })

              if (error) {
                return showError(error)
              }

              if (data?.validateItalianFiscalCode) {
                showSuccess(t('pms.common.customer.valid_fiscal_code'))
              } else {
                showError(t('pms.common.customer.invalid_fiscal_code'))
              }
            }}
          >
            {t('pms.common.customer.fiscal_code_check')}
          </Link>
        </Box>
      }
    >
      <TextField
        onChange={(event) => {
          onChange(event.target.value)
        }}
        fullWidth
        disabled={disabled}
        value={value ?? ''}
        error={Boolean(errorMessage)}
        size={'small'}
      />
    </FormItem>
  )
}
